import {ellipsis} from '../util/ellipsis';
import hideElement from '../util/hide-element';
import showElement from '../util/show-element';
import scrollTo from '../util/scroll-to';
import {register} from '../vendor/theme-scripts/theme-sections';
import {RadioSwatch} from '../features/swatch';
import {tooltipSection} from '../features/tooltip';
import {ComplementaryProducts} from '../features/complementary-products';
import {RecipientForm} from '../features/recipient-form';
import {productStickySection} from '../features/product-sticky';
import {Slider} from '../features/slider';
import {ProductSiblings} from '../features/siblings';
import tabs from '../features/tabs';
import * as a11y from '../vendor/theme-scripts/theme-a11y';
import {initSlider} from '../media/init-slider';
import {ShareButton} from '../features/share';
import {isDesktop} from '../util/media-query';
import {ProductForm} from '../features/product-form';
import {ProductModal} from '../features/product-modal';
import {ProductModel} from '../features/product-model';
import {PickupAvailability} from '../features/pickup-availability';
import Zoom from '../media/zoom';

const selectors = {
  addToCart: '[data-add-to-cart]',
  priceWrapper: '[data-price-wrapper]',
  productImage: '[data-product-image]',
  productJson: '[data-product-json]',
  form: '[data-product-form]',
  dataSectionId: 'data-section-id',
  dataCartBar: 'data-cart-bar',
  cartBar: '#cart-bar',
  cartBarAdd: 'data-add-to-cart-bar',
  cartBarScroll: 'data-cart-bar-scroll',
  productSubmitAdd: '.product__submit__add',
  toggleTruncateHolder: '[data-truncated-holder]',
  toggleTruncateButton: '[data-truncated-button]',
  toggleTruncateContent: '[data-truncated-content]',
  toggleTruncateContentAttr: 'data-truncated-content',
  formWrapper: '[data-form-wrapper]',
  productVariants: '[data-product-variants]',
  slider: '[data-slider]',
  sliderIndex: 'data-slider-index',
};

const classes = {
  expanded: 'is-expanded',
  visible: 'is-visible',
  loading: 'is-loading',
  added: 'is-added',
};

const attributes = {
  enableImageZoom: 'data-image-zoom-enable',
};

const sections = {};

/**
 * Product section constructor.
 * @param {string} container - selector for the section container DOM element
 */
class Product {
  constructor(section) {
    this.section = section;
    this.container = section.container;
    this.id = this.container.getAttribute(selectors.dataSectionId);
    this.sliders = this.container.querySelectorAll(selectors.slider);
    this.slider = [];
    this.truncateElementHolder = this.container.querySelector(selectors.toggleTruncateHolder);
    this.truncateElement = this.container.querySelector(selectors.toggleTruncateContent);
    this.formWrapper = this.container.querySelector(selectors.formWrapper);
    this.cartBarExist = this.container.getAttribute(selectors.dataCartBar) === 'true';
    this.cartBar = this.container.querySelector(selectors.cartBar);
    this.scrollToTop = this.scrollToTop.bind(this);
    this.scrollEvent = (e) => this.scrollEvents(e);
    this.resizeEvent = (e) => this.resizeEvents(e);
    this.unlockTimer = 0;
    this.accessibility = a11y;

    if (this.container.hasAttribute(attributes.enableImageZoom)) {
      new Zoom(this.section);
    }

    document.addEventListener('theme:product:galleryUpdate', ()=> {
      if (this.container.hasAttribute(attributes.enableImageZoom)) {
        new Zoom(this.section);
      }
    });

    if (this.truncateElementHolder && this.truncateElement) {
      setTimeout(() => this.truncateText(), 50);

      document.addEventListener('theme:resize', this.resizeEvent);
    }

    // Stop parsing if we don't have the product json script tag when loading
    // section in the Theme Editor
    const productJson = this.container.querySelector(selectors.productJson);
    if ((productJson && !productJson.innerHTML) || !productJson) {
      return;
    }

    const productJsonHandle = JSON.parse(productJson.innerHTML).handle;
    let recentObj = {};
    if (productJsonHandle) {
      recentObj = {
        handle: productJsonHandle,
      };
    }

    // Record recently viewed products when the product page is loading
    Shopify.Products.recordRecentlyViewed(recentObj);

    this.form = this.container.querySelector(selectors.form);

    if (this.sliders.length) {
      this.sliders.forEach((slider, index) => {
        slider.setAttribute(selectors.sliderIndex, index);
        this.slider.push(new Slider(this.container, slider));
      });
    }

    if (this.cartBarExist) {
      this.initCartBar();
      document.addEventListener('theme:scroll', this.scrollEvent);
    }
  }

  scrollEvents(e) {
    if (this.cartBarExist) {
      this.cartBarScroll();
    }
  }

  resizeEvents(e) {
    this.truncateText();
  }

  truncateText() {
    if (this.truncateElementHolder.classList.contains(classes.visible)) return;
    const styles = this.truncateElement.querySelectorAll('style');
    if (styles.length) {
      styles.forEach((style) => {
        this.truncateElementHolder.prepend(style);
      });
    }

    const truncateElementCloned = this.truncateElement.cloneNode(true);
    const truncateElementClass = this.truncateElement.getAttribute(selectors.toggleTruncateContentAttr);
    const truncateNextElement = this.truncateElement.nextElementSibling;
    if (truncateNextElement) {
      truncateNextElement.remove();
    }

    this.truncateElement.parentElement.append(truncateElementCloned);

    const truncateAppendedElement = this.truncateElement.nextElementSibling;
    truncateAppendedElement.classList.add(truncateElementClass);
    truncateAppendedElement.removeAttribute(selectors.toggleTruncateContentAttr);

    showElement(truncateAppendedElement);

    ellipsis(truncateAppendedElement, 5, {
      replaceStr: '',
      delimiter: ' ',
    });

    hideElement(truncateAppendedElement);

    if (this.truncateElement.innerHTML !== truncateAppendedElement.innerHTML) {
      this.truncateElementHolder.classList.add(classes.expanded);
    } else {
      truncateAppendedElement.remove();
      this.truncateElementHolder.classList.remove(classes.expanded);
    }

    this.toggleTruncatedContent(this.truncateElementHolder);
  }

  toggleTruncatedContent(holder) {
    const toggleButton = holder.querySelector(selectors.toggleTruncateButton);
    if (toggleButton) {
      toggleButton.addEventListener('click', (e) => {
        e.preventDefault();
        holder.classList.remove(classes.expanded);
        holder.classList.add(classes.visible);
      });
    }
  }

  initCartBar() {
    // Submit product form on cart bar button click
    this.cartBarBtn = this.cartBar.querySelector(selectors.productSubmitAdd);
    if (this.cartBarBtn) {
      this.cartBarBtn.addEventListener('click', (e) => {
        e.preventDefault();

        if (e.currentTarget.hasAttribute(selectors.cartBarAdd)) {
          if (theme.settings.cartDrawerEnabled) {
            e.currentTarget.classList.add(classes.loading);
            e.currentTarget.setAttribute('disabled', 'disabled');
          }

          this.form.querySelector(selectors.addToCart).dispatchEvent(
            new Event('click', {
              bubbles: true,
            })
          );
        } else if (e.currentTarget.hasAttribute(selectors.cartBarScroll)) {
          this.scrollToTop();
        }
      });

      if (this.cartBarBtn.hasAttribute(selectors.cartBarAdd)) {
        document.addEventListener('theme:product:add-error', this.scrollToTop);
      }
    }
  }

  scrollToTop() {
    const productVariants = this.container.querySelector(selectors.productVariants);
    const scrollTarget = isDesktop() ? this.container : productVariants ? productVariants : this.form;
    const scrollTargetTop = scrollTarget.getBoundingClientRect().top;

    scrollTo(isDesktop() ? scrollTargetTop : scrollTargetTop - 10);
  }

  cartBarScroll() {
    const scrolled = window.pageYOffset;
    const element = theme.variables.productPageSticky && this.formWrapper ? this.formWrapper : this.form;

    if (element && this.cartBar) {
      const formOffset = element.offsetTop;
      const formHeight = element.offsetHeight;
      const checkPosition = scrolled > formOffset + formHeight;

      this.cartBar.classList.toggle(classes.visible, checkPosition);
    }
  }

  onUnload() {
    document.removeEventListener('theme:product:add-error', this.scrollToTop);

    if (this.truncateElementHolder && this.truncateElement) {
      document.removeEventListener('theme:resize', this.resizeEvent);
    }

    if (this.cartBarExist) {
      document.removeEventListener('theme:scroll', this.scrollEvent);
    }
  }

  onBlockSelect(e) {
    const slider = e.srcElement.closest(selectors.slider);
    if (slider && this.slider.length) {
      const sliderIndex = slider.hasAttribute(selectors.sliderIndex) ? slider.getAttribute(selectors.sliderIndex) : 0;
      if (!this.slider[sliderIndex]) return;
      this.slider[sliderIndex].onBlockSelect(e);
    }
  }

  onBlockDeselect(e) {
    const slider = e.srcElement.closest(selectors.slider);
    if (slider && this.slider.length) {
      const sliderIndex = slider.hasAttribute(selectors.sliderIndex) ? slider.getAttribute(selectors.sliderIndex) : 0;
      if (!this.slider[sliderIndex]) return;
      this.slider[sliderIndex].onBlockDeselect(e);
    }
  }
}

const productSection = {
  onLoad() {
    sections[this.id] = new Product(this);
  },
  onUnload(e) {
    sections[this.id].onUnload(e);
  },
  onBlockSelect(e) {
    sections[this.id].onBlockSelect(e);
  },
  onBlockDeselect(e) {
    sections[this.id].onBlockDeselect(e);
  },
};

register('product', [productSection, tooltipSection, tabs, productStickySection, initSlider]);

if (!customElements.get('complementary-products')) {
  customElements.define('complementary-products', ComplementaryProducts);
}

if (!customElements.get('pickup-availability')) {
  customElements.define('pickup-availability', PickupAvailability);
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}

if (!customElements.get('product-modal')) {
  customElements.define('product-modal', ProductModal);
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}

if (!customElements.get('product-siblings')) {
  customElements.define('product-siblings', ProductSiblings);
}

if (!customElements.get('radio-swatch')) {
  customElements.define('radio-swatch', RadioSwatch);
}

if (!customElements.get('recipient-form')) {
  customElements.define('recipient-form', RecipientForm);
}

if (!customElements.get('share-button')) {
  customElements.define('share-button', ShareButton);
}
